import { useState } from 'react'
import { KeyRound, Plus, Trash2, Send, CheckCircle2, ArrowRight, Loader2 } from 'lucide-react'
import { PageHero } from '../components/PageHero'
import { useSeo } from '../lib/useSeo'
import {
  verifyCode, getMachineTypes, getMachineMarques, envoyerDemande,
  type Prefill, type MachineTypes, type Machine,
} from '../api/enlevement'

/** Emplacements machine du formulaire. Trois jusqu'au 24/10/2026 : un client a
 *  dû décrire ses trois machines en trop dans le texte libre, deux fois à dix
 *  mois d'intervalle. Le plafond est touché 9 % du temps en 2026 (contre 1,6 %
 *  en 2025). Six couvre tout l'historique.
 *  Doit rester égal à MACHINES_MAX côté serveur : c'est lui qui refuse au-delà. */
const MACHINES_MAX = 6


const champ =
  'w-full rounded-xl border border-navy/15 bg-white px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-orange disabled:bg-sand disabled:text-ink/50'
const label = 'block text-xs font-semibold uppercase tracking-wide text-ink/55 mb-1.5'

/** Ce que le client tape → « XXXX-XXXX », la forme imprimée sur son étiquette.
 *
 * Le code fait 8 caractères et rien d'autre : on retire ce qui n'est ni lettre
 * ni chiffre (le client tape parfois le tiret, ou colle le code avec un espace),
 * on coupe à 8, et on repose le tiret au milieu. Le champ ne peut donc pas
 * contenir autre chose qu'un code de la bonne longueur — plus de saisie qui
 * part en vrille sur 12 caractères avant que le serveur ne la refuse.
 *
 * Le tiret n'est qu'un repère de lecture : le serveur le retire à son tour
 * (`_normalise_code`), il n'est pas stocké.
 */
export function formaterCode(saisie: string): string {
  const net = saisie.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8)
  return net.length > 4 ? `${net.slice(0, 4)}-${net.slice(4)}` : net
}

type Etape = 'code' | 'formulaire' | 'envoye'

/** Le `detail` d'une erreur FastAPI n'est pas toujours une phrase : une erreur
 *  de validation renvoie une LISTE d'objets, et la donner telle quelle à React
 *  vide la page (« Objects are not valid as a React child »). On ne montre donc
 *  que du texte, quoi qu'il arrive. */
function messageErreur(err: any, defaut: string): string {
  const d = err?.response?.data?.detail
  if (typeof d === 'string' && d.trim()) return d
  if (Array.isArray(d)) {
    const msgs = d.map((x) => (typeof x?.msg === 'string' ? x.msg : '')).filter(Boolean)
    if (msgs.length) return msgs.join(' · ')
  }
  return defaut
}


export function DemandeEnlevement() {
  useSeo(
    "Demande d'enlèvement & entretien d'hiver — Maison Gilles (Namur)",
    "Service d'enlèvement et d'entretien hivernal de votre tondeuse ou robot tondeuse. Maison Gilles, à Jambes (Namur) — Honda & Husqvarna.",
  )
  const [etape, setEtape] = useState<Etape>('code')

  // Étape 1
  const [code, setCode] = useState('')
  const [validationToken, setValidationToken] = useState('')
  const [codeError, setCodeError] = useState<string | null>(null)
  const [verif, setVerif] = useState(false)

  // Étape 2
  const [form, setForm] = useState<Prefill & { remarque_generale: string }>({ remarque_generale: '' })
  const [types, setTypes] = useState<MachineTypes>({})
  const [marquesCache, setMarquesCache] = useState<Record<string, string[]>>({})
  const [machines, setMachines] = useState<Machine[]>([{ machine: '', marque: '', remarque: '' }])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [numero, setNumero] = useState<number | null>(null)

  async function soumettreCode(e: React.FormEvent) {
    e.preventDefault()
    setVerif(true); setCodeError(null)
    try {
      const { prefill, validation_token } = await verifyCode(code)
      setValidationToken(validation_token)
      const t = await getMachineTypes()
      setTypes(t)
      setForm({ ...prefill, remarque_generale: '' })
      setEtape('formulaire')
    } catch (err: any) {
      setCodeError(messageErreur(err, 'Code invalide ou service indisponible.'))
    } finally {
      setVerif(false)
    }
  }

  async function ensureMarques(type: string) {
    if (!type || marquesCache[type]) return
    try {
      const m = await getMachineMarques(type)
      setMarquesCache((c) => ({ ...c, [type]: m }))
    } catch {
      setMarquesCache((c) => ({ ...c, [type]: [] }))
    }
  }

  const setMachine = (i: number, k: keyof Machine, v: string) =>
    setMachines((arr) => arr.map((m, j) => (j === i ? { ...m, [k]: v, ...(k === 'machine' ? { marque: '' } : {}) } : m)))

  // Machine 2 (puis 3) n'est proposée que quand les précédentes sont complètes
  // — type ET marque : on n'enlève pas une machine qu'on ne sait pas nommer.
  const machineComplete = (m: Machine) => !!(m.machine && m.marque)
  const toutesCompletes = machines.every(machineComplete)

  function ajouterMachine() {
    if (machines.length < MACHINES_MAX && toutesCompletes)
      setMachines((arr) => [...arr, { machine: '', marque: '', remarque: '' }])
  }
  function retirerMachine(i: number) {
    setMachines((arr) => arr.filter((_, j) => j !== i).map((m, j) => {
      // En remontant d'un cran, une machine peut porter un type que la place
      // n'accepte pas (la machine 1 est limitée aux grosses machines) : on la
      // vide plutôt que de laisser un choix illégal dans le menu.
      const slot = types[`Machine${j + 1}_Type`] || []
      return m.machine && !slot.includes(m.machine) ? { ...m, machine: '', marque: '' } : m
    }))
  }

  async function envoyer(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true); setError(null)
    try {
      const res = await envoyerDemande({
        validation_token: validationToken,
        nom: form.nom || '',
        adresse: form.adresse,
        code_postal: form.code_postal,
        localite: form.localite,
        telephone1: form.telephone1,
        telephone2: form.telephone2,
        email: form.email,
        numero_client: form.numero_client,
        machines: machines.filter((m) => m.machine || m.marque || m.remarque),
        remarque_generale: form.remarque_generale,
      })
      setNumero(res.numero_demande)
      setEtape('envoye')
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (err: any) {
      setError(messageErreur(err, 'Une erreur est survenue. Réessayez ou appelez-nous.'))
    } finally {
      setBusy(false)
    }
  }

  // ── Écran de confirmation ──
  if (etape === 'envoye') {
    return (
      <>
        <PageHero eyebrow="Demande d'enlèvement" titre="Demande envoyée" />
        <section className="bg-white py-20">
          <div className="mx-auto max-w-xl px-5 text-center">
            <CheckCircle2 className="mx-auto h-14 w-14 text-orange" />
            <p className="mt-5 text-lg text-ink/80">
              Votre demande {numero ? <strong>n° {numero}</strong> : ''} a bien été enregistrée.
              Nous vous recontacterons pour planifier notre passage.
            </p>
          </div>
        </section>
      </>
    )
  }

  return (
    <>
      <PageHero eyebrow="Demande d'enlèvement" titre="Demande d'enlèvement et d'entretien de votre matériel de jardin">
        <p>
          Ce service d'enlèvement et d'entretien s'organise une fois par an. Indiquez vos coordonnées
          et sélectionnez votre matériel pour planifier facilement notre passage.
        </p>
      </PageHero>

      <section className="bg-sand py-14">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">

          {/* ───── Étape 1 : code personnel ───── */}
          {etape === 'code' && (
            <form onSubmit={soumettreCode} className="rounded-3xl border border-navy/10 bg-white p-7 text-center shadow-card sm:p-10">
              <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-navy/10 text-navy">
                <KeyRound className="h-7 w-7" />
              </span>
              <h2 className="mt-5 font-display text-2xl font-bold text-navy">Insérez votre code personnel</h2>
              <p className="mx-auto mt-2 max-w-md text-sm text-ink/60">
                Ce code figure sur le courrier que vous avez reçu de Maison Gilles. Il est nécessaire pour
                accéder au formulaire de demande.
              </p>
              {/* Le code est imprimé en majuscules et groupé (KM7R-3PXB) : on
                  affiche ce que le client voit sur son courrier. Le serveur
                  remet de toute façon la saisie en forme (majuscules, tirets et
                  espaces retirés) — le champ ne fait que l'aider à voir qu'il
                  tape juste, et `formaterCode` garantit qu'il ne peut pas
                  taper autre chose. */}
              <input
                value={code}
                onChange={(e) => setCode(formaterCode(e.target.value))}
                autoFocus
                maxLength={9}
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                placeholder="XXXX-XXXX"
                aria-label="Votre code personnel"
                className="mx-auto mt-6 block w-full max-w-xs rounded-xl border border-navy/15 bg-white px-4 py-3 text-center text-lg font-semibold uppercase tracking-widest outline-none focus:border-orange"
              />
              {codeError && (
                <div className="mx-auto mt-4 max-w-md rounded-lg bg-orange/10 px-3 py-2 text-sm text-orange-dk">
                  {codeError}
                </div>
              )}
              <button
                /* Le code fait 8 caractères plus le tiret : tant que la
                   saisie est incomplète, l'envoyer ne peut que ramener
                   « code incorrect » — et consommer une des 5 tentatives
                   horaires du visiteur. */
                disabled={verif || code.length < 9}
                className="mx-auto mt-6 inline-flex items-center gap-2 rounded-full bg-navy px-8 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-navy-dk disabled:opacity-50"
              >
                {verif ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                {verif ? 'Vérification…' : 'Soumettre'}
              </button>
            </form>
          )}

          {/* ───── Étape 2 : formulaire ───── */}
          {etape === 'formulaire' && (
            <form onSubmit={envoyer} className="space-y-8 rounded-3xl border border-navy/10 bg-white p-7 shadow-card sm:p-9">
              <div className="flex items-center gap-2 rounded-xl bg-green-50 px-4 py-3 text-sm text-green-700">
                <CheckCircle2 className="h-5 w-5 shrink-0" />
                Code validé. Vérifiez vos coordonnées et indiquez votre matériel.
              </div>

              {/* Coordonnées (pré-remplies) */}
              <div>
                <h2 className="font-display text-lg font-bold text-navy">Vos coordonnées</h2>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label htmlFor="dem-nom" className={label}>Nom *</label>
                    <input id="dem-nom" required value={form.nom || ''} onChange={(e) => setForm({ ...form, nom: e.target.value })} className={champ} />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="dem-adresse" className={label}>Adresse</label>
                    <input id="dem-adresse" value={form.adresse || ''} onChange={(e) => setForm({ ...form, adresse: e.target.value })} className={champ} />
                  </div>
                  <div>
                    <label htmlFor="dem-cp" className={label}>Code postal</label>
                    <input id="dem-cp" value={form.code_postal || ''} onChange={(e) => setForm({ ...form, code_postal: e.target.value })} className={champ} />
                  </div>
                  <div>
                    <label htmlFor="dem-localite" className={label}>Localité</label>
                    <input id="dem-localite" value={form.localite || ''} onChange={(e) => setForm({ ...form, localite: e.target.value })} className={champ} />
                  </div>
                  <div>
                    <label htmlFor="dem-tel1" className={label}>Téléphone 1</label>
                    <input id="dem-tel1" value={form.telephone1 || ''} onChange={(e) => setForm({ ...form, telephone1: e.target.value })} className={champ} />
                  </div>
                  <div>
                    <label htmlFor="dem-tel2" className={label}>Téléphone 2</label>
                    <input id="dem-tel2" value={form.telephone2 || ''} onChange={(e) => setForm({ ...form, telephone2: e.target.value })} className={champ} />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="dem-email" className={label}>Adresse mail</label>
                    <input id="dem-email" type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} className={champ} />
                  </div>
                </div>
              </div>

              {/* Machines */}
              <div>
                <h2 className="font-display text-lg font-bold text-navy">Votre matériel</h2>
                <div className="mt-4 space-y-5">
                  {machines.map((m, i) => {
                    const typesSlot = types[`Machine${i + 1}_Type`] || []
                    const marques = m.machine ? marquesCache[m.machine] || [] : []
                    return (
                      <div key={i} className="rounded-2xl border border-navy/10 bg-sand p-5">
                        <div className="flex items-center justify-between">
                          <p className="font-display text-sm font-semibold text-navy">Machine {i + 1}</p>
                          {machines.length > 1 && (
                            <button type="button" onClick={() => retirerMachine(i)} className="text-ink/40 hover:text-orange" aria-label="Retirer">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                        {i === 0 && (
                          <p className="mt-2 rounded-lg border border-orange/30 bg-orange/5 px-3 py-2 text-xs leading-relaxed text-ink/80">
                            Les <b>robots tondeuse</b> ne peuvent pas être choisis comme machine principale. Si vous n’êtes pas en mesure de nous l’amener au magasin, <b>contactez-nous</b> au 081/301194.
                          </p>
                        )}
                        <div className="mt-3 grid gap-4 sm:grid-cols-2">
                          <div>
                            <label className={label}>Quelle est votre machine ?</label>
                            <select
                              value={m.machine}
                              onChange={(e) => { setMachine(i, 'machine', e.target.value); ensureMarques(e.target.value) }}
                              aria-label={`Machine ${i + 1} — type`}
                              className={champ}
                            >
                              <option value="">— Choisir —</option>
                              {typesSlot.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className={label}>Quelle est la marque ?</label>
                            <select
                              value={m.marque}
                              onChange={(e) => setMachine(i, 'marque', e.target.value)}
                              disabled={!m.machine}
                              aria-label={`Machine ${i + 1} — marque`}
                              className={champ}
                            >
                              <option value="">{m.machine ? '— Choisir —' : 'Choisissez d\'abord la machine'}</option>
                              {marques.map((mq) => <option key={mq} value={mq}>{mq}</option>)}
                            </select>
                          </div>
                          <div className="sm:col-span-2">
                            <label className={label}>Avez-vous une remarque ?</label>
                            <input value={m.remarque} onChange={(e) => setMachine(i, 'remarque', e.target.value)} aria-label={`Machine ${i + 1} — remarque`} className={champ} />
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
                {machines.length < MACHINES_MAX && (
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={ajouterMachine}
                      disabled={!toutesCompletes}
                      className="inline-flex items-center gap-2 rounded-full border border-navy/20 px-4 py-2 text-sm font-semibold text-navy transition-colors enabled:hover:border-orange/50 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <Plus className="h-4 w-4" /> Ajouter une autre machine
                    </button>
                    {!toutesCompletes && (
                      <p className="mt-2 text-xs text-ink/50">
                        Choisissez d'abord la machine et la marque ci-dessus.
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Remarque générale */}
              <div>
                <label htmlFor="dem-remarque" className={label}>Remarque générale</label>
                <textarea id="dem-remarque" rows={3} value={form.remarque_generale} onChange={(e) => setForm({ ...form, remarque_generale: e.target.value })} className={champ} />
              </div>

              {error && <div className="rounded-lg bg-orange/10 px-3 py-2 text-sm text-orange-dk">{error}</div>}

              <button disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-navy px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-navy-dk disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {busy ? 'Envoi…' : 'Soumettre'}
              </button>
            </form>
          )}
        </div>
      </section>
    </>
  )
}
